import React, { useState, useEffect, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import styles from './ResultTeen20.module.css';
import "./Result.css";

// Helper: map card code to image
const getCardImage = (cardString) => {
  if (!cardString) return null;
  return `/assets/img/cards/${cardString}.png`;
};

// Helper: split cards into Player A / Player B (first 3 vs next 3)
const parseCards = (cardString) => {
  if (!cardString) return { playerA: [], playerB: [] };

  const cards = cardString.split(',');
  return {
    playerA: cards.slice(0, 3).map(c => {
      const card = c?.trim();
      return card ? getCardImage(card) : null;
    }).filter(Boolean),
    playerB: cards.slice(3, 6).map(c => {
      const card = c?.trim();
      return card ? getCardImage(card) : null;
    }).filter(Boolean)
  };
};

// Helper: parse rdesc (Winner#Part1~Score#Total#Combo#Red/Black)
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};

  const parts = rdesc.split('#');
  const winner = parts[0] || "";
  const part1Full = parts[1] || "";
  let part1 = "", score = "";
  if (part1Full.includes('~')) {
    const p = part1Full.split('~');
    part1 = p[0] || "";
    score = p[1] || "";
  } else {
    part1 = part1Full;
  }
  const total = parts[2] || "";
  const combo = parts[3] || "";
  const redBlack = parts[4] || parts[3] || "";

  return { winner, part1, score, total, combo, redBlack };
};

const ResultTeen20 = ({
  data = [],
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);

  // Fetch last results on mount (type: teen20)
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('teen20');
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            const mappedResults = response.data.data.res.map(item => ({
              mid: item.mid,
              win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
            }));
            setLastResults(mappedResults);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            const mappedResults = response.data.res.map(item => ({
              mid: item.mid,
              win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
            }));
            setLastResults(mappedResults);
          }
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch detail results when navigating between results
  const fetchDetailForIndex = useCallback(async (index) => {
    if (index >= 0 && index < lastResults.length && lastResults[index]?.mid) {
      try {
        const response = await getDetailResults('teen20', lastResults[index].mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            return response.data.data.t1;
          } else if (response?.data?.t1) {
            return response.data.t1;
          }
        }
      } catch (error) {
        console.error(`Error fetching detail for index ${index}:`, error);
      }
    }
    return null;
  }, [lastResults]);

  const handleResultClick = async (result, index) => {
    setSelectedResult(result);
    setCurrentResultIndex(index);
    setLoading(true);
    setShowModal(true);

    let mid = null;
    if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid;
    }

    if (mid) {
      try {
        const response = await getDetailResults('teen20', mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  const getModalData = () => {
    if (!selectedResult) return null;

    const cards = detailResult?.card ? parseCards(detailResult.card) : {
      playerA: [],
      playerB: []
    };

    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winner = detailResult?.winnat || (selectedResult === "A" ? "Player A" : selectedResult === "B" ? "Player B" : "");

    const winValue = detailResult?.win;
    const isPlayerAWinner = winValue === "1" || winner === "Player A" || selectedResult === "A";
    const isPlayerBWinner = winValue === "2" || winner === "Player B" || selectedResult === "B";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "157250124095658",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      playerA: { cards: cards.playerA, isWinner: isPlayerAWinner },
      playerB: { cards: cards.playerB, isWinner: isPlayerBWinner },
      resultDetails: {
        winner: winner,
        part1: rdescData.part1 || "",
        score: rdescData.score || "",
        total: rdescData.total || "",
        combo: rdescData.combo || "",
        redBlack: rdescData.redBlack || "",
      },
    };
  };

  const modalData = getModalData();
  const allResults = lastResults.length > 0
    ? lastResults.map(r => r.win)
    : [...data, ...(gameData?.lrs || []), ...(gameData?.data?.lrs || [])];

  return (
    <>
      <div className={styles.lastResults}>
        {allResults.map((res, index) => (
          <span
            key={index}
            className={`${styles.resultCircle} ${res === "A" ? styles.playerA : styles.playerB}`}
            onClick={() => handleResultClick(res, index)}
            style={{ cursor: res ? 'pointer' : 'default' }}
          >
            {res}
          </span>
        ))}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="20-20 Teenpatti Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <h6 className="text-right">
                  <b>Round Id :</b> {modalData.roundId}
                </h6>
              </div>
            </div>

            <div className="row bottom-border-casino m-t-10">
              <div className="col-md-12">
                <div className="player-image-container row">
                  <div className="col-md-6 text-center player-number">
                    <h4>Player A</h4>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                      {modalData.playerA?.cards?.map((card, index) => (
                        card && (
                          <img
                            key={index}
                            src={card}
                            alt={`Player A Card ${index + 1}`}
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/card-results/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div className="col-md-12 text-center">
                      {modalData.playerA?.isWinner ? (
                        <label className="winner-label bg-success m-t-20">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-20">&nbsp;</label>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 text-center player-number">
                    <h4>Player B</h4>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                      {modalData.playerB?.cards?.map((card, index) => (
                        card && (
                          <img
                            key={index}
                            src={card}
                            alt={`Player B Card ${index + 1}`}
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/card-results/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div className="col-md-12 text-center">
                      {modalData.playerB?.isWinner ? (
                        <label className="winner-label bg-success m-t-20">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-20">&nbsp;</label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

        
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultTeen20;

